import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Mail, Gift, ShoppingBag, Package, Clock, Zap, PhoneCall, CreditCard, CheckCircle2, X, ChevronDown, ChevronLeft, ChevronRight, XCircle, Edit, FileText } from 'lucide-react';
import Header from '../components/common/Header';
import { customerService } from '../services/customerService';
import catalogService from '../services/catalogService';
import UserSidebar from '../components/common/UserSidebar';
import { getImageUrl, generateSlug } from '../utils/helpers';
import { toast } from 'react-toastify';
import { ORDER_STATUS, ORDER_STATUS_CONFIG, PRODUCT_STATUS } from '../utils/constants';

const PromotionAccordion = ({ promo }) => {
    const [isOpen, setIsOpen] = useState(false);
    const promoName = typeof promo === 'string' ? promo : (promo.saleName || promo.name);
    
    if (!promoName) return null;

    return (
        <div className="flex flex-col border border-orange-200/60 rounded-lg overflow-hidden bg-gradient-to-r from-orange-50 to-white w-full mb-1.5 last:mb-0 transition-all shadow-sm">
            <button
                type="button"
                onClick={(e) => { 
                    e.preventDefault(); 
                    e.stopPropagation(); 
                    if(promo.description) setIsOpen(!isOpen); 
                }}
                className={`flex items-center justify-between gap-2 px-2.5 py-2 w-full text-left transition-colors ${promo.description ? 'hover:bg-orange-50 cursor-pointer' : 'cursor-default'}`}
            >
                <div className="flex items-start gap-1.5 text-xs text-tnc-orange w-full min-w-0">
                    <Gift size={14} className="shrink-0 text-tnc-orange mt-0.5" />
                    <span className="font-bold leading-snug line-clamp-2">{promoName}</span>
                </div>
                {promo.description && (
                    <ChevronDown size={14} className={`text-tnc-orange shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                )}
            </button>
            
            {isOpen && promo.description && (
                <div className="px-2.5 pb-2.5 pt-0.5 text-[11px] text-gray-700 bg-white border-t border-orange-100">
                    <div 
                        className="html-content mt-1.5 [&_p]:mb-1 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-md"
                        dangerouslySetInnerHTML={{ __html: String(promo.description).replace(/&nbsp;/g, ' ') }} 
                    />
                </div>
            )}
        </div>
    );
};

const UserOrdersPage = () => {
    const [orders, setOrders] = useState([]);
    const [allSales, setAllSales] = useState([]); 
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('ALL');

    const [paymentMethods, setPaymentMethods] = useState([]);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [activeOrderForPayment, setActiveOrderForPayment] = useState(null);
    const [selectedPaymentId, setSelectedPaymentId] = useState('');
    const [isUpdatingPayment, setIsUpdatingPayment] = useState(false);

    // State cho Modal Sửa thông tin giao hàng
    const [isDeliveryModalOpen, setIsDeliveryModalOpen] = useState(false);
    const [activeOrderForDelivery, setActiveOrderForDelivery] = useState(null);
    const [isUpdatingDelivery, setIsUpdatingDelivery] = useState(false);
    const [deliveryForm, setDeliveryForm] = useState({
        receiverName: '',
        receiverPhone: '',
        receiverEmail: '',
        fullAddress: '',
        orderNote: ''
    });

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    useEffect(() => {
        const fetchPaymentMethods = async () => {
            try {
                const res = await customerService.getPublicPaymentMethods();
                const data = res?.data || res;
                if (Array.isArray(data)) {
                    setPaymentMethods(data);
                } else if (data && Array.isArray(data.data)) {
                    setPaymentMethods(data.data);
                } else {
                    setPaymentMethods([]);
                }
            } catch (error) {
                console.error("Lỗi lấy danh sách PT thanh toán:", error);
            }
        };
        fetchPaymentMethods();
    }, []);

    const fetchMyOrders = useCallback(async () => {
        setLoading(true);
        try {
            const [ordersRes, salesRes] = await Promise.all([
                customerService.getMyOrders(),
                catalogService.getPublicSales().catch(() => [])
            ]);

            const responseData = ordersRes.data || ordersRes;
            setOrders(Array.isArray(responseData) ? responseData : []);
            
            const salesData = Array.isArray(salesRes?.data) ? salesRes.data : (Array.isArray(salesRes) ? salesRes : []);
            setAllSales(salesData);
        } catch (error) {
            console.error("Lỗi tải đơn hàng:", error);
            setOrders([]); 
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMyOrders();
    }, [fetchMyOrders]);

    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab]);

    const handleCancelOrder = async (orderId) => {
        if (window.confirm("Bạn có chắc chắn muốn hủy đơn hàng này không? Hành động này không thể hoàn tác.")) {
            try {
                await customerService.cancelOrder(orderId);
                toast.success("Đã hủy đơn hàng thành công!");
                fetchMyOrders(); 
            } catch (error) {
                toast.error(error.response?.data || "Lỗi khi hủy đơn hàng!");
            }
        }
    };

    const handleOpenPaymentModal = (order) => {
        setActiveOrderForPayment(order);
        setSelectedPaymentId(order.paymentMethod?.id || '');
        setIsPaymentModalOpen(true);
    };

    const handleUpdatePayment = async () => {
        if (!selectedPaymentId) {
            toast.warn("Vui lòng chọn 1 phương thức thanh toán!");
            return;
        }
        const currentMethodId = activeOrderForPayment.paymentMethod?.id || '';
        if (String(selectedPaymentId) === String(currentMethodId)) {
            setIsPaymentModalOpen(false); 
            return;
        }

        setIsUpdatingPayment(true);
        try {
            await customerService.updateOrderPaymentMethod(activeOrderForPayment.orderId, selectedPaymentId);
            toast.success("Đã cập nhật phương thức thanh toán!");
            setIsPaymentModalOpen(false);
            fetchMyOrders(); 
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data || "Lỗi khi cập nhật phương thức thanh toán!");
        } finally {
            setIsUpdatingPayment(false);
        }
    };

    // --- Các hàm xử lý cho sửa thông tin giao hàng ---
    const handleOpenDeliveryModal = (order) => {
        setActiveOrderForDelivery(order);
        setDeliveryForm({
            receiverName: order.orderCustomerName || order.receiverName || order.account?.fullName || '',
            receiverPhone: order.orderPhone || order.receiverPhone || order.account?.phone || '',
            receiverEmail: order.orderEmail || order.receiverEmail || order.account?.email || '',
            fullAddress: order.orderAddress || order.fullAddress || '',
            orderNote: order.orderNote || order.note || ''
        });
        setIsDeliveryModalOpen(true);
    };

    const handleDeliveryFormChange = (e) => {
        const { name, value } = e.target;
        setDeliveryForm(prev => ({ ...prev, [name]: value }));
    };

    const handleUpdateDelivery = async () => {
        if (!deliveryForm.receiverName || !deliveryForm.receiverPhone || !deliveryForm.fullAddress) {
            toast.warn("Vui lòng điền đầy đủ Tên, Số điện thoại và Địa chỉ!");
            return;
        }

        setIsUpdatingDelivery(true);
        try {
            await customerService.updateOrderDeliveryInfo(activeOrderForDelivery.orderId, deliveryForm);
            toast.success("Đã cập nhật thông tin giao hàng và ghi chú!");
            setIsDeliveryModalOpen(false);
            fetchMyOrders(); 
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data || "Lỗi khi cập nhật thông tin giao hàng!");
        } finally {
            setIsUpdatingDelivery(false);
        }
    };

    const formatPrice = (price) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(price || 0);

    const renderOrderStatus = (status) => {
        const config = ORDER_STATUS_CONFIG[status];
        if (!config) return null;
        const Icon = config.Icon;
        return (
            <span className={`flex items-center gap-1.5 ${config.color} ${config.bg} px-3 py-1.5 rounded-full text-xs font-bold border border-current shadow-sm`}>
                {Icon && <Icon size={14} />} {config.text}
            </span>
        );
    };

    const filteredOrders = useMemo(() => {
        if (activeTab === 'ALL') return orders;
        if (activeTab === 'HISTORY') {
            return orders.filter(o => [ORDER_STATUS.DELIVERED, ORDER_STATUS.CANCELLED, ORDER_STATUS.RETURNED].includes(o.orderStatus));
        }
        return orders.filter(o => String(o.orderStatus) === activeTab);
    }, [orders, activeTab]);

    const TABS = [
        { id: 'ALL', label: 'Tất cả' },
        { id: String(ORDER_STATUS.PENDING), label: ORDER_STATUS_CONFIG[ORDER_STATUS.PENDING]?.text || 'Chờ xác nhận' },
        { id: String(ORDER_STATUS.CONFIRMED), label: ORDER_STATUS_CONFIG[ORDER_STATUS.CONFIRMED]?.text || 'Đã xác nhận' },
        { id: String(ORDER_STATUS.IN_WAREHOUSE), label: ORDER_STATUS_CONFIG[ORDER_STATUS.IN_WAREHOUSE]?.text || 'Đã tới kho' },
        { id: String(ORDER_STATUS.SHIPPING), label: ORDER_STATUS_CONFIG[ORDER_STATUS.SHIPPING]?.text || 'Đang giao' },
        { id: 'HISTORY', label: 'Lịch sử' }
    ];

    const totalPages = Math.ceil(filteredOrders.length / itemsPerPage) || 1;
    const paginatedOrders = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredOrders.slice(start, start + itemsPerPage);
    }, [filteredOrders, currentPage, itemsPerPage]);

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Header />
            <main className="container mx-auto px-4 py-8 flex-grow max-w-6xl">
                <div className="flex flex-col md:flex-row gap-6">
                    <UserSidebar />

                    <div className="flex-1">
                        <div className="bg-gradient-to-b from-orange-50 to-white rounded-xl shadow-sm border border-orange-200/60 p-6 mb-4 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                <ShoppingBag className="text-tnc-orange" /> Lịch sử Đơn hàng
                            </h2>
                        </div>

                        {!loading && orders.length > 0 && (
                            <div className="flex overflow-x-auto scrollbar-hide gap-2 mb-6 bg-white/60 backdrop-blur-sm p-2 rounded-xl shadow-sm border border-orange-200/60">
                                {TABS.map(tab => (
                                    <button 
                                        key={tab.id} 
                                        onClick={() => setActiveTab(tab.id)} 
                                        className={`whitespace-nowrap px-4 py-2 rounded-lg font-bold text-sm transition-all ${activeTab === tab.id ? 'bg-[#002855] text-white shadow-md' : 'bg-transparent text-gray-500 hover:bg-orange-50 hover:text-tnc-orange'}`}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                        )}

                        {loading ? (
                            <div className="text-center py-10 text-gray-500">Đang tải lịch sử đơn hàng...</div>
                        ) : filteredOrders.length === 0 ? (
                            <div className="bg-gradient-to-b from-orange-50 to-white rounded-xl shadow-sm border border-orange-200/60 p-10 text-center">
                                <Package size={64} className="text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500">Không có đơn hàng nào trong mục này.</p>
                                <Link to="/" className="inline-block mt-4 px-6 py-2 bg-tnc-orange text-white font-bold rounded-lg hover:bg-tnc-orange-hover">Tiếp tục mua sắm</Link>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {paginatedOrders.map(order => {
                                    const grandTotal = order.orderPrice + (order.orderShippingFee || 0);
                                    const paymentMethodName = order.paymentMethod?.name || order.paymentMethodName || 'Thanh toán tiền mặt';
                                    
                                    const contactName = order.orderCustomerName || order.receiverName || order.account?.fullName || 'Khách hàng';
                                    const contactPhone = order.orderPhone || order.receiverPhone || order.account?.phone || 'Không có SĐT';
                                    const contactEmail = order.orderEmail || order.receiverEmail || order.account?.email || 'Không có email';
                                    const contactAddress = order.orderAddress || order.fullAddress || 'Không có địa chỉ';
                                    const contactNote = order.orderNote || order.note || '';

                                    return (
                                        <div key={order.orderId} className="bg-white rounded-xl shadow-sm border border-orange-200/60 overflow-hidden hover:shadow-md transition-shadow hover:border-tnc-orange/50">
                                            <div className="flex flex-col sm:flex-row sm:items-center justify-between px-6 py-4 border-b border-orange-100 bg-gradient-to-r from-orange-50/50 to-white gap-4">
                                                
                                                <div className="flex flex-col">
                                                    <span className="font-bold text-gray-800 text-lg">Mã ĐH: #{order.orderId}</span>
                                                    <span className="text-sm text-gray-500 mt-0.5 flex items-center gap-1">
                                                        <Clock size={12}/> {new Date(order.orderDate).toLocaleString('vi-VN')}
                                                    </span>
                                                    
                                                    {/* Khu vực hiển thị thông tin và Ghi chú */}
                                                    <div className="mt-2 text-xs text-gray-600 bg-white/60 p-2.5 rounded-lg border border-orange-100 shadow-sm inline-block min-w-[250px] max-w-md relative">
                                                        {Number(order.orderStatus) === ORDER_STATUS.PENDING && (
                                                            <button 
                                                                onClick={() => handleOpenDeliveryModal(order)}
                                                                className="absolute top-2 right-2 p-1.5 bg-blue-50 text-blue-600 rounded-md transition-colors hover:bg-blue-600 hover:text-white shadow-sm border border-blue-100"
                                                                title="Thay đổi thông tin giao hàng & ghi chú"
                                                            >
                                                                <Edit size={14} />
                                                            </button>
                                                        )}
                                                        <p className="flex items-center gap-1.5 mb-1.5 border-b border-orange-50 pb-1.5 pr-6">
                                                            <span className="font-bold text-gray-700">👤 {contactName}</span>
                                                        </p>
                                                        <p className="flex items-center gap-1.5 mb-1">
                                                            <PhoneCall size={12} className="text-gray-400"/> 
                                                            <span className="font-medium shrink-0">SĐT:</span> 
                                                            <span className="font-bold">{contactPhone}</span>
                                                        </p>
                                                        <p className="flex items-center gap-1.5">
                                                            <Mail size={12} className="text-gray-400"/>
                                                            <span className="font-medium shrink-0">Email:</span> 
                                                            <span className="font-bold text-blue-600">{contactEmail}</span>
                                                        </p>
                                                        <p className="flex items-start gap-1.5 mt-1 border-t border-orange-50 pt-1.5 pr-4">
                                                            <MapPin size={12} className="text-gray-400 mt-0.5 shrink-0"/>
                                                            <span className="font-medium shrink-0">Địa chỉ:</span> 
                                                            <span className="font-bold text-gray-700 leading-snug">{contactAddress}</span>
                                                        </p>
                                                        {contactNote && (
                                                            <p className="flex items-start gap-1.5 mt-1 border-t border-orange-50 pt-1.5 pr-4">
                                                                <FileText size={12} className="text-gray-400 mt-0.5 shrink-0"/>
                                                                <span className="font-medium shrink-0">Ghi chú:</span> 
                                                                <span className="font-bold text-gray-700 leading-snug italic">{contactNote}</span>
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-3">
                                                    {order.isPaid === true ? (
                                                        <span className="flex items-center gap-1 text-green-700 bg-green-100 px-3 py-1.5 rounded-full text-xs font-bold shadow-sm">
                                                            <CheckCircle2 size={14}/> Đã thanh toán
                                                        </span>
                                                    ) : (
                                                        <span className="flex items-center gap-1 text-orange-700 bg-orange-100 px-3 py-1.5 rounded-full text-xs font-bold shadow-sm">
                                                            <Clock size={14}/> Chưa thanh toán
                                                        </span>
                                                    )}
                                                    {renderOrderStatus(order.orderStatus)}
                                                </div> 
                                            </div>

                                            <div className="p-6 space-y-6">
                                                {order.items && order.items.map((orderItem, idx) => {
                                                    const unitPrice = orderItem.orderItemQuantity > 0 ? (orderItem.orderItemTotalPrice / orderItem.orderItemQuantity) : 0;
                                                    const itemData = orderItem.item || {};
                                                    const isCompletelyDeleted = !orderItem.item;
                                                    
                                                    const rawImg = itemData.itemImage || (itemData.images?.length > 0 ? itemData.images[0].itemImageUrl : null);
                                                    const releasePrice = itemData.itemReleasePrice || 0;
                                                    const salePrice = itemData.itemSalePrice || 0;
                                                    const isFlashSale = orderItem.isFlashSale;
                                                    const productId = itemData.itemId;
                                                    const itemName = itemData.itemName || 'Sản phẩm không còn tồn tại';
                                                    const itemCode = itemData.itemCode || 'N/A';

                                                    const isItemActive = !isCompletelyDeleted && 
                                                                        ![PRODUCT_STATUS.HIDDEN, PRODUCT_STATUS.OUT_OF_STOCK, PRODUCT_STATUS.DISCONTINUED].includes(itemData.itemStatus);
                                                    
                                                    const isNormalSale = !isFlashSale && salePrice > 0 && unitPrice <= salePrice;
                                                    const priceColorClass = isFlashSale ? 'text-red-600' : (isNormalSale ? 'text-tnc-orange' : 'text-[#002855]');

                                                    const rawSaleIds = itemData.saleIds || [];
                                                    const safeSaleIds = rawSaleIds.map(id => String(id));
                                                    const itemPromotions = allSales.filter(sale => safeSaleIds.includes(String(sale.saleId)));

                                                    return (
                                                        <div key={idx} className="flex items-start gap-4 pb-6 border-b border-orange-50 last:border-0 last:pb-0">
                                                            {isItemActive ? (
                                                                <Link to={`/san-pham/${generateSlug(itemName)}-id${productId}`} className="relative shrink-0 block group">
                                                                    <div className="w-20 h-20 bg-white rounded-lg border border-orange-100 group-hover:border-tnc-orange p-1 transition-colors">
                                                                        <img src={getImageUrl(rawImg)} alt={itemName} className="w-full h-full object-contain mix-blend-multiply" onError={(e) => { e.target.onerror = null; e.target.src = getImageUrl(null); }} />
                                                                    </div>
                                                                    {isFlashSale && <div className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md"><Zap size={10} fill="currentColor"/></div>}
                                                                </Link>
                                                            ) : (
                                                                <div className="relative shrink-0 block opacity-60 cursor-not-allowed" title="Sản phẩm không khả dụng">
                                                                    <div className="w-20 h-20 bg-white rounded-lg border border-gray-100 p-1">
                                                                        <img src={getImageUrl(rawImg)} alt="Ngừng kinh doanh" className="w-full h-full object-contain mix-blend-multiply grayscale" onError={(e) => { e.target.onerror = null; e.target.src = getImageUrl(null); }} />
                                                                    </div>
                                                                </div>
                                                            )}

                                                            <div className="flex-1 min-w-0">
                                                                {isItemActive ? (
                                                                    <Link 
                                                                        to={`/san-pham/${generateSlug(itemName)}-id${productId}`} 
                                                                        className="font-bold text-sm md:text-base text-gray-800 hover:text-tnc-orange line-clamp-2 leading-snug transition-colors"
                                                                    >
                                                                        {itemName}
                                                                    </Link>
                                                                ) : (
                                                                    <span className="font-bold text-sm md:text-base text-gray-400 line-clamp-2 leading-snug" title="Sản phẩm đã ngừng kinh doanh hoặc hết hàng">
                                                                        {itemName} (Ngừng kinh doanh)
                                                                    </span>
                                                                )}
                                                                
                                                                <div className="text-xs text-gray-500 mt-1 font-medium mb-1.5">Mã SP: <span className="font-mono text-blue-600 font-bold">{itemCode}</span></div>
                                                                
                                                                {!isFlashSale && itemPromotions.length > 0 && (
                                                                    <div className="mt-2 mb-2.5 flex flex-col gap-1.5 pr-4">
                                                                        {itemPromotions.map((promo, idxSale) => (
                                                                            <PromotionAccordion key={idxSale} promo={promo} />
                                                                        ))}
                                                                    </div>
                                                                )}

                                                                <div className="flex items-center gap-2 mt-1.5">
                                                                    <p className="text-sm text-gray-500 font-bold">x{orderItem.orderItemQuantity}</p>
                                                                    {isFlashSale ? <span className="text-[10px] text-red-600 font-bold bg-red-100 px-1.5 py-0.5 rounded">Flash Sale</span> : isNormalSale && <span className="text-[10px] text-orange-600 font-bold bg-orange-100 px-1.5 py-0.5 rounded">Giảm giá</span>}
                                                                </div>
                                                            </div>

                                                            <div className="text-right shrink-0 flex flex-col items-end">
                                                                <span className={`font-bold text-base md:text-lg ${priceColorClass}`}>
                                                                    {formatPrice(orderItem.orderItemTotalPrice)}
                                                                </span>
                                                                
                                                                <div className="flex flex-col items-end mt-0.5 space-y-0.5">
                                                                    {isFlashSale && salePrice > 0 && salePrice > unitPrice && (
                                                                        <span className="text-[11px] text-tnc-orange line-through block font-medium">
                                                                            {formatPrice(salePrice * orderItem.orderItemQuantity)}
                                                                        </span>
                                                                    )}
                                                                    {releasePrice > 0 && releasePrice > unitPrice && releasePrice !== salePrice && (
                                                                        <span className="text-[11px] text-gray-400 line-through block">
                                                                            {formatPrice(releasePrice * orderItem.orderItemQuantity)}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            <div className="bg-gradient-to-r from-orange-50/30 to-white border-t border-orange-100 flex flex-col">
                                                {Number(order.orderStatus) === ORDER_STATUS.PENDING && (
                                                    <div className="px-6 pt-4">
                                                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-3 text-yellow-700 animate-pulse shadow-sm">
                                                            <PhoneCall size={18} className="shrink-0"/>
                                                            <p className="text-xs sm:text-sm font-bold leading-relaxed">
                                                                Thành Nam PC sẽ gọi điện và gửi Email để xác nhận hoặc thông báo tiến độ đơn hàng. Vui lòng chú ý điện thoại và hộp thư nhé!
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-orange-100/60">
                                                    <div className="flex items-center gap-3">
                                                        <div className="p-2.5 bg-white border border-orange-200/60 rounded-xl shadow-sm">
                                                            <CreditCard size={22} className="text-[#002855]" />
                                                        </div>
                                                        <div>
                                                            <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider mb-0.5">
                                                                Phương thức thanh toán
                                                            </p>
                                                            <p className="text-sm font-bold text-gray-800 leading-none">
                                                                {paymentMethodName}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    
                                                    {Number(order.orderStatus) === ORDER_STATUS.PENDING && String(order.isPaid) !== 'true' && (
                                                        <button 
                                                            onClick={() => handleOpenPaymentModal(order)}
                                                            className="w-full sm:w-auto px-4 py-2.5 bg-white border-2 border-orange-200/60 text-tnc-orange rounded-xl text-xs font-bold hover:bg-orange-50 hover:border-tnc-orange transition-all shadow-sm flex items-center justify-center gap-1.5"
                                                        >
                                                            ĐỔI PHƯƠNG THỨC THANH TOÁN
                                                        </button>
                                                    )}
                                                </div>

                                                <div className="px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-6">
                                                    <div className="w-full sm:w-auto">
                                                        {Number(order.orderStatus) === ORDER_STATUS.PENDING ? (
                                                            <button 
                                                                onClick={() => handleCancelOrder(order.orderId)} 
                                                                className="w-full sm:w-auto px-5 py-2.5 border-2 border-red-100 text-red-500 bg-white hover:bg-red-50 hover:text-red-600 hover:border-red-200 rounded-xl font-bold transition-all text-sm flex items-center justify-center gap-2 shadow-sm"
                                                            >
                                                                <XCircle size={18} /> Hủy đơn hàng
                                                            </button>
                                                        ) : Number(order.orderStatus) === ORDER_STATUS.CANCELLED ? (
                                                            <span className="inline-block w-full sm:w-auto text-center text-xs text-red-600 font-bold uppercase tracking-wide bg-red-50 px-4 py-3 rounded-xl border border-red-100">
                                                                Đơn hàng đã hủy
                                                            </span>
                                                        ) : (
                                                            <span className="text-sm text-gray-400 italic hidden sm:inline-block">
                                                                Đơn hàng đang xử lý, không thể hủy.
                                                            </span>
                                                        )}
                                                    </div>

                                                    <div className="w-full sm:w-auto flex flex-col items-end">
                                                        <div className="flex items-center gap-1.5 mb-1">
                                                            <span className="text-sm text-gray-500 font-medium">Tổng cộng (</span>
                                                            <span className="text-sm font-bold text-[#002855]">
                                                                {order.items?.reduce((total, item) => total + item.orderItemQuantity, 0) || 0} món
                                                            </span>
                                                            <span className="text-sm text-gray-500 font-medium">):</span>
                                                        </div>
                                                        <div className="flex items-baseline gap-2">
                                                            <span className="text-2xl font-black text-tnc-orange tracking-tight">
                                                                {formatPrice(grandTotal)}
                                                            </span>
                                                        </div>
                                                        
                                                        {Number(order.orderStatus) === ORDER_STATUS.PENDING ? (
                                                            <p className="text-[11px] text-tnc-orange font-bold mt-1 bg-orange-50 px-2 py-0.5 rounded border border-orange-100">
                                                                Phí vận chuyển: Đang chờ xác nhận
                                                            </p>
                                                        ) : order.orderShippingFee > 0 ? (
                                                            <p className="text-[11px] text-gray-500 font-medium mt-1">
                                                                (Đã bao gồm <span className="font-bold text-gray-700">{formatPrice(order.orderShippingFee)}</span> phí vận chuyển)
                                                            </p>
                                                        ) : (
                                                            <p className="text-[11px] text-green-600 font-bold mt-1 bg-green-50 px-2 py-0.5 rounded border border-green-100">
                                                                Miễn phí vận chuyển
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                                {totalPages > 1 && (
                                    <div className="border-t border-orange-200/60 pt-4 mt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
                                        <div className="text-xs text-gray-500 font-bold">
                                            Hiển thị <span className="text-gray-900">{(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, filteredOrders.length)}</span> trên <span className="text-gray-900">{filteredOrders.length}</span> đơn hàng
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button 
                                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                                disabled={currentPage === 1}
                                                className="p-1.5 border border-gray-200 rounded-lg text-gray-500 hover:bg-orange-50 hover:text-tnc-orange hover:border-tnc-orange disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                                <ChevronLeft size={16} />
                                            </button>
                                            
                                            <div className="flex items-center px-3 text-sm font-bold text-gray-700">
                                                Trang {currentPage} / {totalPages}
                                            </div>

                                            <button 
                                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                                disabled={currentPage === totalPages}
                                                className="p-1.5 border border-gray-200 rounded-lg text-gray-500 hover:bg-orange-50 hover:text-tnc-orange hover:border-tnc-orange disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                                <ChevronRight size={16} />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {/* MODAL THANH TOÁN (Cũ) */}
            {isPaymentModalOpen && activeOrderForPayment && (
                <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="bg-[#002855] p-4 text-white flex justify-between items-center shrink-0">
                            <h3 className="font-bold text-lg flex items-center gap-2">
                                <CreditCard size={20} className="text-tnc-orange" /> 
                                Thanh toán Đơn hàng #{activeOrderForPayment.orderId}
                            </h3>
                            <button onClick={() => setIsPaymentModalOpen(false)} className="hover:bg-white/20 p-1.5 rounded-full transition-colors">
                                <X size={20} />
                            </button>
                        </div>
                        
                        <div className="p-6 overflow-y-auto flex-1 bg-white">
                            <div className="flex justify-between items-center bg-gradient-to-r from-orange-50 to-white p-4 rounded-xl border border-orange-200/60 shadow-sm mb-6">
                                <div className="flex flex-col">
                                    <span className="text-gray-600 font-bold">Cần thanh toán:</span>
                                    {Number(activeOrderForPayment.orderStatus) === ORDER_STATUS.PENDING && (
                                        <span className="text-[11px] text-orange-500 italic mt-0.5">
                                            * Chưa bao gồm phí vận chuyển
                                        </span>
                                    )}
                                </div>
                                <span className="text-2xl font-black text-tnc-orange">
                                    {formatPrice(activeOrderForPayment.orderPrice + (activeOrderForPayment.orderShippingFee || 0))}
                                </span>
                            </div>

                            <p className="font-bold text-gray-800 mb-3">Chọn phương thức thanh toán:</p>
                            <div className="space-y-4 mb-6">
                                {paymentMethods.length > 0 ? (
                                    paymentMethods.map((method) => {
                                        const isSelected = String(selectedPaymentId) === String(method.id);
                                        return (
                                            <label 
                                                key={method.id} 
                                                className={`flex flex-col p-4 border-2 rounded-xl cursor-pointer transition-all ${
                                                    isSelected 
                                                    ? 'border-tnc-orange bg-orange-50/50 shadow-sm' 
                                                    : 'border-orange-200/60 bg-white hover:border-tnc-orange hover:shadow-md'
                                                }`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <input 
                                                            type="radio" 
                                                            name="paymentMethod" 
                                                            checked={isSelected}
                                                            onChange={() => setSelectedPaymentId(method.id)}
                                                            className="w-5 h-5 cursor-pointer appearance-none border-2 border-gray-300 rounded-full checked:border-tnc-orange checked:bg-tnc-orange checked:ring-2 checked:ring-white checked:ring-inset transition-all focus:ring-0 focus:ring-offset-0"
                                                            style={{ boxShadow: isSelected ? '0 0 0 2px white inset' : 'none' }}
                                                        />
                                                        <div className="flex-1 font-bold text-gray-800">{method.name}</div>
                                                    </div>
                                                    <div className="text-gray-400">
                                                        <div className="p-1.5 bg-white rounded-md shadow-sm border border-orange-100 shrink-0">
                                                            {method.imageUrl ? (
                                                                <img src={getImageUrl(method.imageUrl)} alt="icon" className="w-6 h-6 object-contain" />
                                                            ) : (
                                                                <CreditCard size={24} className="text-tnc-orange" />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {isSelected && (method.imageUrl || method.content) && (
                                                    <div className="px-4 pb-4 animate-in slide-in-from-top-2 duration-300 mt-2">
                                                        <div className="bg-white rounded-xl p-4 border border-orange-100 shadow-sm">
                                                            {method.imageUrl && (
                                                                <div className="mt-4 pt-4 border-t border-orange-200/60 flex flex-col items-center">
                                                                    <p className="text-[10px] font-bold text-gray-400 mb-2 uppercase">Mã QR Thanh toán</p>
                                                                    <img 
                                                                        src={getImageUrl(method.imageUrl)} 
                                                                        alt="QR" 
                                                                        className="w-40 h-40 object-contain mx-auto border border-orange-100 rounded-lg p-1" 
                                                                    />
                                                                </div>
                                                            )}
                                                            {method.content && (
                                                                <div className="text-sm text-gray-700 bg-white p-3 rounded-lg border border-orange-100 w-full text-center shadow-sm leading-relaxed mt-3">
                                                                    <p className="whitespace-pre-wrap">{method.content}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </label>
                                        );
                                    })
                                ) : (
                                    <div className="text-center py-10 text-gray-400 italic">Không có phương thức khả dụng</div>
                                )}
                            </div>
                        </div>

                        <div className="p-4 border-t border-orange-200/60 bg-white shrink-0 flex gap-3">
                            <button 
                                onClick={() => setIsPaymentModalOpen(false)}
                                className="px-6 py-2.5 bg-gray-100 text-gray-600 font-bold rounded-xl hover:bg-gray-200 transition-colors"
                            >
                                Đóng
                            </button>
                            <button 
                                onClick={handleUpdatePayment}
                                disabled={isUpdatingPayment || !selectedPaymentId}
                                className="flex-1 bg-tnc-orange text-white font-bold py-2.5 rounded-xl hover:bg-tnc-orange-hover transition-colors disabled:opacity-50"
                            >
                                {isUpdatingPayment ? 'Đang lưu...' : 'Lưu phương thức & Thanh toán'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL SỬA THÔNG TIN GIAO HÀNG (Mới) */}
            {isDeliveryModalOpen && activeOrderForDelivery && (
                <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="bg-[#002855] p-4 text-white flex justify-between items-center shrink-0">
                            <h3 className="font-bold text-lg flex items-center gap-2">
                                <Edit size={20} className="text-tnc-orange" /> 
                                Sửa thông tin Đơn hàng #{activeOrderForDelivery.orderId}
                            </h3>
                            <button onClick={() => setIsDeliveryModalOpen(false)} className="hover:bg-white/20 p-1.5 rounded-full transition-colors">
                                <X size={20} />
                            </button>
                        </div>
                        
                        <div className="p-6 overflow-y-auto flex-1 bg-white space-y-4 text-sm">
                            {/* ĐÃ XÓA KHỐI CHỌN ĐỊA CHỈ Ở ĐÂY */}

                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Tên người nhận <span className="text-red-500">*</span></label>
                                <input type="text" name="receiverName" value={deliveryForm.receiverName} onChange={handleDeliveryFormChange} className="w-full border border-gray-300 rounded-lg p-2.5 outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange" placeholder="Nhập tên người nhận" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Số điện thoại <span className="text-red-500">*</span></label>
                                <input type="text" name="receiverPhone" value={deliveryForm.receiverPhone} onChange={handleDeliveryFormChange} className="w-full border border-gray-300 rounded-lg p-2.5 outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange" placeholder="Nhập số điện thoại" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Email</label>
                                <input type="email" name="receiverEmail" value={deliveryForm.receiverEmail} onChange={handleDeliveryFormChange} className="w-full border border-gray-300 rounded-lg p-2.5 outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange" placeholder="Nhập email" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Địa chỉ giao hàng chi tiết <span className="text-red-500">*</span></label>
                                <textarea name="fullAddress" value={deliveryForm.fullAddress} onChange={handleDeliveryFormChange} rows="3" className="w-full border border-gray-300 rounded-lg p-2.5 outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange resize-none" placeholder="Nhập địa chỉ nhận hàng"></textarea>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Ghi chú đơn hàng</label>
                                <textarea name="orderNote" value={deliveryForm.orderNote} onChange={handleDeliveryFormChange} rows="2" className="w-full border border-gray-300 rounded-lg p-2.5 outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange resize-none" placeholder="Ghi chú thêm về việc giao hàng (ví dụ: giao giờ hành chính, gọi điện trước khi giao...)"></textarea>
                            </div>
                        </div>

                        <div className="p-4 border-t border-orange-200/60 bg-white shrink-0 flex gap-3">
                            <button onClick={() => setIsDeliveryModalOpen(false)} className="px-6 py-2.5 bg-gray-100 text-gray-600 font-bold rounded-xl hover:bg-gray-200 transition-colors">
                                Hủy
                            </button>
                            <button onClick={handleUpdateDelivery} disabled={isUpdatingDelivery} className="flex-1 bg-tnc-orange text-white font-bold py-2.5 rounded-xl hover:bg-tnc-orange-hover transition-colors disabled:opacity-50">
                                {isUpdatingDelivery ? 'Đang lưu...' : 'Lưu thay đổi'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default UserOrdersPage;